import { useState, type ReactNode, type InputHTMLAttributes, type SelectHTMLAttributes } from 'react';

/** Small shared primitives, kept in one file so the screens stay about behaviour. */

export function Button({
  children,
  onClick,
  variant = 'default',
  disabled,
  type = 'button',
  title,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'primary' | 'danger' | 'ghost';
  disabled?: boolean;
  type?: 'button' | 'submit';
  title?: string;
}) {
  const styles: Record<string, string> = {
    default: 'bg-ink-800 hover:bg-ink-700 text-ink-100 border-ink-700',
    primary: 'bg-accent/90 hover:bg-accent text-ink-950 border-accent font-medium',
    danger: 'bg-transparent hover:bg-bad/15 text-bad border-bad/40',
    ghost: 'bg-transparent hover:bg-ink-800 text-ink-300 border-transparent',
  };
  return (
    <button
      type={type}
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`px-3 py-1.5 rounded-md border text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${styles[variant]}`}
    >
      {children}
    </button>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wide text-ink-500 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-xs text-ink-500 mt-1">{hint}</span>}
    </label>
  );
}

const inputClass =
  'w-full bg-ink-900 border border-ink-800 rounded-md px-2.5 py-1.5 text-sm text-ink-100 ' +
  'focus:outline-none focus:border-accent/70 placeholder:text-ink-700';

export function Input(props: InputHTMLAttributes<HTMLInputElement> & { mono?: boolean }) {
  const { mono, className, ...rest } = props;
  return <input {...rest} className={`${inputClass} ${mono ? 'mono' : ''} ${className ?? ''}`} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  const { className, ...rest } = props;
  return <select {...rest} className={`${inputClass} ${className ?? ''}`} />;
}

export function Panel({ title, actions, children }: { title?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="bg-ink-900 border border-ink-800 rounded-lg overflow-hidden">
      {(title || actions) && (
        <header className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-ink-800">
          <h2 className="text-sm font-medium text-ink-100">{title}</h2>
          <div className="flex items-center gap-2">{actions}</div>
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export type Health = 'healthy' | 'stale' | 'failing' | 'idle';

export function healthOf(source: {
  consecutive_failures: number;
  last_success_at: string | null;
  interval_seconds: number;
}): Health {
  if (source.consecutive_failures > 0) return 'failing';
  if (!source.last_success_at) return 'idle';
  const age = (Date.now() - new Date(source.last_success_at).getTime()) / 1000;
  // Stale after three missed cycles: one late poll is noise, three is a pattern.
  return age > source.interval_seconds * 3 ? 'stale' : 'healthy';
}

export function Dot({ health }: { health: Health }) {
  const colour: Record<Health, string> = {
    healthy: 'bg-good',
    stale: 'bg-warn',
    failing: 'bg-bad',
    idle: 'bg-ink-700',
  };
  return <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${colour[health]}`} title={health} />;
}

/** Copy button that confirms it worked — the core interaction of the Loxone handoff. */
export function Copy({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1200);
        });
      }}
      className={`px-2 py-1 rounded border text-xs transition-colors ${
        done ? 'border-good text-good' : 'border-ink-700 text-ink-300 hover:bg-ink-800'
      }`}
    >
      {done ? 'Copied' : label}
    </button>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="text-sm text-ink-500 py-6 text-center">{children}</p>;
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div className="text-sm text-bad bg-bad/10 border border-bad/30 rounded-md px-3 py-2 break-words">{children}</div>
  );
}

export function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}
